import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Define a type for the slice state
export interface ConfirmationState {
  email: {
    request_notification: {
      [key: string]: {
        status: number | boolean | null;
        data: any;
        loading: boolean;
      };
    };
  };
  payment: {
    confirmation: {
      [key: string]: {
        status: number | boolean | null;
        data: any;
        loading: boolean;
      };
    };
    cancellation: {
      [key: string]: {
        status: number | boolean | null;
        data: any;
        loading: boolean;
      };
    };
    two_factor_authentication: {
      data: any;
      loading: boolean;
      redirecting: boolean;
      attempted: boolean;
      errors: string[];
    };
    opayo: {
      threeDSecure: {
        data: null,
        loading: boolean,
        errors: [],
        success: boolean,
      }
    };
  };
}

// Define the initial state using that type
const initialState: ConfirmationState = {
  email: {
    request_notification: {},
  },
  payment: {
    confirmation: {},
    cancellation: {},
    two_factor_authentication: {
      data: null,
      loading: false,
      redirecting: false,
      attempted: false,
      errors: [],
    },
    opayo: {
      threeDSecure: {
        data: null,
        loading: false,
        errors: [],
        success: false,
      }
    }
  },
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Update request status from email notification
export const updateRequestStatusEmailNotification = createAsyncThunk(
  "confirmation/request/status/email/get",
  async (
    obj: {
      status: string;
      requestId: string;
      propertyId: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Retrieve auth data
      let res = await axios.get(
        `/api/voice/requests/email/${obj.status}/${obj.requestId}/${obj.propertyId}/`,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Payment confirmation
export const confirmPayment = createAsyncThunk(
  "confirmation/payment/confirm/get",
  async (
    obj: {
      transactionId: string;
      propertyId: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Retrieve auth data
      let res = await axios.get(
        `/api/payment/confirm/${obj.transactionId}/${obj.propertyId}/`,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Payment cancellation
export const cancelPayment = createAsyncThunk(
  "confirmation/payment/cancel/get",
  async (
    obj: {
      transactionId: string;
      propertyId: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Retrieve auth data
      let res = await axios.get(
        `/api/payment/cancel/${obj.transactionId}/${obj.propertyId}/`,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Validate payment pin
export const validatePaymentPin = createAsyncThunk(
  "confirmation/payment/pin/validate/post",
  async (
    obj: {
      pin: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Build URL
      const url = `/api/payment/2fa/pin/`;

      // Build body
      const body = { pin: obj.pin };
      const config = {};

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Confirm 3D Secure transaction
export const threeDSecureConfirm = createAsyncThunk(
  "portal/payment/opayo/notification/3d-secure/post",
  async (
    obj: {
      propertyId: string | undefined;
      transactionId: string | undefined;
      threeDSession: string | null;
      cRes: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {

      // Build body
      const body = {
        threeDSSessionData: obj.threeDSession,
        cres: obj.cRes,
      };

      // Build config
      const config = { headers: { "Content-Type": "application/json" } };

      const res = await axios.post(`/api/payment/opayo/transaction/3d-secure/${obj.propertyId}/${obj.transactionId}/`, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const confirmationSlice = createSlice({
  name: "confirmation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Update request status from email notification
      .addCase(
        updateRequestStatusEmailNotification.pending,
        (state, action) => {
          const { requestId } = action.meta.arg;
          state.email.request_notification[requestId] = {
            status: null,
            data: null,
            loading: true,
          };
        },
      )
      .addCase(
        updateRequestStatusEmailNotification.fulfilled,
        (state, action) => {
          const { requestId } = action.meta.arg;
          state.email.request_notification[requestId] = {
            status: action.payload.data.status,
            data: action.payload.data.response,
            loading: false,
          };
        },
      )
      .addCase(
        updateRequestStatusEmailNotification.rejected,
        (state, action) => {
          const { requestId } = action.meta.arg;
          state.email.request_notification[requestId] = {
            status: false,
            data: "Slice failed",
            loading: false,
          };
        },
      )

      // Payment confirmation
      .addCase(confirmPayment.pending, (state, action) => {
        const { transactionId } = action.meta.arg;
        state.payment.confirmation[transactionId] = {
          status: null,
          data: null,
          loading: true,
        };
      })
      .addCase(confirmPayment.fulfilled, (state, action) => {
        const { transactionId } = action.meta.arg;
        state.payment.confirmation[transactionId] = {
          status: action.payload.data.status,
          data: action.payload.data.response,
          loading: false,
        };
      })
      .addCase(confirmPayment.rejected, (state, action) => {
        const { transactionId } = action.meta.arg;
        state.payment.confirmation[transactionId] = {
          status: false,
          data: "Slice failed",
          loading: false,
        };
      })

      // Payment cancellation
      .addCase(cancelPayment.pending, (state, action) => {
        const { transactionId } = action.meta.arg;
        state.payment.cancellation[transactionId] = {
          status: null,
          data: null,
          loading: true,
        };
      })
      .addCase(cancelPayment.fulfilled, (state, action) => {
        const { transactionId } = action.meta.arg;
        state.payment.cancellation[transactionId] = {
          status: action.payload.data.status,
          data: action.payload.data.response,
          loading: false,
        };
      })
      .addCase(cancelPayment.rejected, (state, action) => {
        const { transactionId } = action.meta.arg;
        state.payment.cancellation[transactionId] = {
          status: false,
          data: "Slice failed",
          loading: false,
        };
      })

      // Validate payment pin
      .addCase(validatePaymentPin.pending, (state) => {
        state.payment.two_factor_authentication.loading = true;
        state.payment.two_factor_authentication.redirecting = false;
        state.payment.two_factor_authentication.attempted = false;
      })
      .addCase(validatePaymentPin.fulfilled, (state, action) => {
        state.payment.two_factor_authentication.data = action.payload.data.data;
        state.payment.two_factor_authentication.loading = false;
        state.payment.two_factor_authentication.redirecting = true;
        state.payment.two_factor_authentication.attempted = true;
      })
      .addCase(validatePaymentPin.rejected, (state) => {
        state.payment.two_factor_authentication.loading = false;
        state.payment.two_factor_authentication.redirecting = false;
        state.payment.two_factor_authentication.attempted = true;
      })

      // 3D Secure confirmation
      .addCase(threeDSecureConfirm.pending, (state, action) => {
        state.payment.opayo.threeDSecure.loading = true;
      })
      .addCase(threeDSecureConfirm.fulfilled, (state, action) => {
        state.payment.opayo.threeDSecure.loading = false;
        state.payment.opayo.threeDSecure.data = action.payload.data;
        state.payment.opayo.threeDSecure.success = true;
      })
      .addCase(threeDSecureConfirm.rejected, (state, action) => {
        state.payment.opayo.threeDSecure.success = false;
        state.payment.opayo.threeDSecure.loading = false;
      })
  },
});

export default confirmationSlice.reducer;
